// api/casting.ts — casting search + generate. Uses the shared client (apiPost).
import { apiGet, apiPost } from "./client.ts";

export interface NeededPersona {
  gender?: "F" | "M";
  ageRange?: string; ethnicity?: string; style?: string;
  physicalAttributes?: Record<string, string>;
  personas?: string[]; niche?: string;
  capability?: "talking_head" | "motion";
}
export interface CastingCandidate {
  stockId: string; displayName: string | null; gender: string | null; previewUrl: string | null;
  ageRange: string | null; ethnicity: string | null; style: string | null;
  capability: { talkingHead: boolean; motion: boolean };
  score: number; matched: string[];
}

export const castingApi = {
  listVoices: () => apiGet<{ voices: Array<{ id: string; name: string }> }>("/admin/characters/voices"),
  roster: (neededPersona: NeededPersona) =>
    apiPost<{ roster: CastingCandidate[]; ignored: string[] }>("/admin/casting/roster", { neededPersona }),

  /** Growth's niche-first flow: the LLM infers the audience-fit persona attributes for a
   *  niche, then the SAME roster scoring above filters/ranks characters by them — one
   *  call, one result. `ignored` carries anything dropped (an off-catalog LLM guess, or,
   *  if the LLM call itself failed, that failure message) — non-fatal either way; a
   *  failure degrades to the full unfiltered roster, never blocks seeing characters. */
  forNiche: (niche: string) =>
    apiPost<{ roster: CastingCandidate[]; neededPersona: NeededPersona; ignored: string[] }>(
      "/admin/casting/for-niche", { niche },
    ),

  /** Generate a character on demand. Reuses POST /admin/characters (source:"generate"). The generation
   *  block is the SAME catalog vocabulary as the casting search → the new avatar is born searchable. */
  generate: (p: NeededPersona & { displayName?: string; visibility?: "public" | "private"; voiceCatalogId: string }) => {
    if (!p.gender || !p.ageRange || !p.ethnicity || !p.style) {
      throw new Error("generate needs gender + ageRange + ethnicity + style");
    }
    if (!p.voiceCatalogId) throw new Error("generate needs a voiceCatalogId (required by the character API)");
    return apiPost<{ stockId: string; state: string }>("/admin/characters", {
      source: "generate",
      displayName: p.displayName ?? null,
      visibility: p.visibility ?? "public",
      voiceCatalogId: p.voiceCatalogId,   // REQUIRED top-level by CreateCharacterSchema
      generation: {
        gender: p.gender, ageRange: p.ageRange, ethnicity: p.ethnicity, style: p.style,
        niche: p.niche, physicalAttributes: p.physicalAttributes, personas: p.personas,
      },
    });
  },
  // poll a character's state until ready (the page uses this after generate)
  getCharacter: (stockId: string) =>
    apiGet<{ character: { stockId: string; state: string; previewUrl: string | null; displayName: string | null } }>(`/admin/characters/${stockId}`),
};
